import type { IAmplifyUser } from 'data/types/amplify.types';

// An invitation email's sign-in link names the account it was issued for
// (`/login?username=<encoded>&pass=<tmp>`). Corporate lessees hold one OPP login
// per unit, so one person opens many of these links from a single browser and the
// account named in the link routinely differs from the one already in session.

// Mapped off IAmplifyUser so renaming one of its fields breaks the build here
// rather than quietly comparing against nothing.
type TAccountIdentifiers = {
  [K in 'username' | 'email' | 'phoneNumber']?: IAmplifyUser[K] | null;
};

// The same phone number reaches us as `+16025551234` from a Cognito username, as
// `16025551234` once the login form strips the `+`, and formatted from user
// attributes. Dropping a NANP country code reconciles those without merging
// genuinely different numbers — comparing only trailing digits would make
// `+442079460958` and `+12079460958` look like one account. Results are
// namespaced so a number can never match an address.
export const normalizeAccountIdentifier = (
  raw?: string | null,
): string | null => {
  const value = raw?.trim();
  if (!value) {
    return null;
  }
  if (/^\+?[\d\s().-]+$/.test(value)) {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      return null;
    }
    const national =
      digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return `phone:${national}`;
  }
  // No address contains whitespace. One that does arrived mangled: in a query
  // string a space is only ever a decoded `+`, so putting the `+` back recovers
  // a plus-addressed mailbox rather than declining to compare it.
  return `id:${value.replace(/\s+/g, '+').toLowerCase()}`;
};

// Cognito may expose an account under its username, email or phone attribute, so
// any one of them matching means the link and the session are the same account.
export const linkTargetsAnotherAccount = (
  linkUsername: string | null | undefined,
  authUser: TAccountIdentifiers | null | undefined,
): boolean => {
  if (!linkUsername?.trim() || !authUser) {
    // The link names nobody, or there is no session for it to differ from.
    // Nothing to defer to, so behaviour is left exactly as it was.
    return false;
  }
  const target = normalizeAccountIdentifier(linkUsername);
  if (!target || !(target.startsWith('phone:') || target.includes('@'))) {
    // The value names neither a mailbox nor a number, so there is no invited
    // sign-in to defer to — the login flow shows the provider chooser for it
    // rather than a prefilled form. Deferring anyway would let any crafted
    // `?username=` strand a signed-in visitor on the login page.
    return false;
  }
  const sessionIdentifiers = [
    authUser.username,
    authUser.email,
    authUser.phoneNumber,
  ]
    .map(normalizeAccountIdentifier)
    .filter((identifier): identifier is string => identifier !== null);

  if (!sessionIdentifiers.length) {
    // The link names an account but the session exposes nothing to compare it
    // against. Answering "same account" would hand the visitor back the unit
    // they were already in, which is the defect this guards, so an undecidable
    // comparison counts as a different account: it only ever defers to the login
    // form, which the visitor can complete.
    return true;
  }
  return !sessionIdentifiers.includes(target);
};
